// "The lane" — what eTX found. Provenance context bar, the identified-
// opportunity hero, the KPI row (now with a gross-profit tile), an aging
// alarm ribbon, per-channel cards and the walkaround-fed damage-rate strip.

import { ArrowUp, ArrowDown, ArrowUpRight } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { ChapterSection, MiniSpark, StatTile, KpiCard, Bullet, FillBar } from "../room-chrome"
import {
  CHANNEL_SPARK, HEADLINE, HIGH_VALUE_FLOOR, INDUSTRY_CLOSE_RATE, INK, MINT, MINT_PALE,
  SOURCES, fmt$, type SourceKey,
} from "../room-model"
import type { Economics, Headline, deriveChannelCards, deriveEvaluations, deriveProfit } from "../derive"

type ChannelCards = ReturnType<typeof deriveChannelCards>
type Evaluations = ReturnType<typeof deriveEvaluations>
type Profit = ReturnType<typeof deriveProfit>

export function LaneChapter({
  first, d, profit, evaluations, channelCards, economics, afterHoursConsumer, allChannels, channelLabel, channelTitle, rangeLabel,
  onSource, hasFilters, filteredEmpty, onClearDims,
}: {
  first?: boolean
  d: Headline
  profit: Profit
  evaluations: Evaluations
  channelCards: ChannelCards
  /** Program economics — leadership (full) view only; null hides the card. */
  economics: Economics | null
  afterHoursConsumer: number
  allChannels: boolean
  channelLabel: string
  channelTitle: string
  rangeLabel: string
  onSource: (key: string) => void
  hasFilters: boolean
  filteredEmpty: boolean
  onClearDims: () => void
}) {
  const trendChip = (v: number) =>
    d.hasPrior ? (
      <span className={cx("flex items-center gap-0.5 font-semibold", v >= 0 ? "text-emerald-700" : "text-[#993C1D]")}>
        {v >= 0 ? <ArrowUp className="size-3" aria-hidden /> : <ArrowDown className="size-3" aria-hidden />}
        {Math.abs(v)}% vs prior {rangeLabel}
      </span>
    ) : (
      <span className="text-[#8B897E]">no prior {rangeLabel} to compare</span>
    )

  const blendedPct = Math.round(profit.blendedMargin * 100)
  const gpSpark = d.valueSpark.map(v => v * profit.blendedMargin)
  const closeDiff = d.closeRate - INDUSTRY_CLOSE_RATE

  // Damage-rate strip — walkaround-backed when available, estimate-derived
  // (with a caveat) otherwise.
  const stripEvaluated = evaluations.hasData ? evaluations.evaluated : d.vehiclesEvaluated
  const stripWithDamage = evaluations.hasData ? evaluations.withDamage : d.vehiclesWithDamage
  const stripRate = evaluations.hasData ? evaluations.rate : d.damageRate

  return (
    <ChapterSection id="lane" eyebrow="Opportunity" title={`${channelTitle} pipeline snapshot`} audience="For the lane" first={first}>
      {/* Context / provenance bar */}
      <div className="flex items-center flex-wrap gap-y-1 rounded-xl border border-[#E3E1DA] px-4 py-2.5 mb-4 text-[12.5px] text-[#6E6C64]" role="region" aria-label="Data collection context">
        <span className="pr-3.5">{channelTitle} · Field collection</span>
        <span className="px-3.5 border-l border-[#E3E1DA]">
          {evaluations.hasData
            ? <><strong className="text-zinc-800 tabular-nums">{evaluations.evaluated}</strong> vehicles evaluated · <strong className="text-zinc-800 tabular-nums">{d.total}</strong> estimates</>
            : <><strong className="text-zinc-800 tabular-nums">{d.total}</strong> estimates generated</>}
        </span>
        <span className="pl-3.5 border-l border-[#E3E1DA]">Conducted on-site by eTX ImpaXt</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <KpiCard label="Open estimates" value={String(d.openCount)}
            sub={<>backlog · <span className="tabular-nums">{d.total}</span> new in {rangeLabel}</>}
            viz={<MiniSpark values={d.countSpark} />} />
          <KpiCard label="Avg estimate value" value={fmt$(d.avgValue)}
            sub={trendChip(d.avgValueTrend)}
            viz={<MiniSpark values={d.valueSpark} />} />
          <KpiCard label="Est. gross profit" value={fmt$(profit.total)}
            sub={<>{blendedPct}% blended margin on pipeline</>}
            viz={<MiniSpark values={gpSpark} accent="#0F6E56" />} />
          <KpiCard label="Close rate" value={`${d.closeRate}%`}
            sub={<>industry benchmark <strong className="text-zinc-600">{INDUSTRY_CLOSE_RATE}%</strong></>}
            viz={
              <Bullet value={d.closeRate} target={INDUSTRY_CLOSE_RATE} max={Math.max(INDUSTRY_CLOSE_RATE * 2, d.closeRate * 1.2)}
                note={<span className={cx("font-semibold", closeDiff >= 0 ? "text-[#0F6E56]" : "text-amber-700")}>{closeDiff >= 0 ? "+" : ""}{closeDiff} pts vs benchmark</span>} />
            } />
      </div>

      {/* Channel breakdown (All view — chips are the drill-in) */}
      {allChannels && (() => {
        const channelTotal = Math.max(channelCards.reduce((s, c) => s + c.identified, 0), 1)
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            {channelCards.map((c, i) => (
              <button key={c.key} onClick={() => onSource(c.key)}
                className="animate-element text-left rounded-2xl border border-[#ECEBE6] bg-white p-4 flex flex-col min-w-0 hover:border-[#1D9E75]/50 hover:shadow-sm transition-all duration-150 group"
                style={{ animationDelay: `${80 + i * 40}ms` }}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8B897E] mb-2 flex items-center justify-between">
                  {c.label}
                  <ArrowUpRight className="size-3 text-[#CFCDC4] group-hover:text-[#1D9E75] transition-colors" aria-hidden />
                </p>
                <p className="text-[22px] font-bold tabular-nums text-zinc-900 leading-none tracking-tight">{fmt$(c.identified)}</p>
                <p className="text-[11.5px] text-[#8B897E] mt-1.5 tabular-nums">
                  {c.count} estimate{c.count !== 1 ? "s" : ""} · {rangeLabel}
                  {c.key === "consumer_intake" && c.surfaceLine && (
                    <span className="block mt-0.5">{c.surfaceLine}</span>
                  )}
                  {c.key === "consumer_intake" && afterHoursConsumer > 0 && (
                    <span className="block text-[#0F6E56] font-medium mt-0.5">{fmt$(afterHoursConsumer)} caught while closed</span>
                  )}
                </p>
                <div className="mt-auto pt-3">
                  <div className="h-1.5 rounded-full bg-[#F1F0EB] overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300" style={{ width: `${(c.identified / channelTotal) * 100}%`, background: c.color }} />
                  </div>
                  <p className="text-[10px] text-[#8B897E] tabular-nums mt-1">{Math.round((c.identified / channelTotal) * 100)}% of identified</p>
                </div>
              </button>
            ))}
          </div>
        )
      })()}

      {/* Program economics — the leadership money card (full view only) */}
      {economics && (
        <div className="animate-element rounded-2xl border border-[#ECEBE6] bg-white p-5 mb-3">
          <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
            <p className="text-sm font-semibold text-zinc-900">Program economics</p>
            <p className="text-xs text-[#8B897E]">what eTX finds, captures, and costs</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <StatTile label="Captured this month" value={fmt$(economics.capturedMTD)} sub="completed repair orders" tone="mint" />
            <StatTile label="Value per scan" value={fmt$(economics.perScan)} sub={economics.scanBasis === "walkaround" ? "captured $ per walkaround · 30d" : "captured $ per estimate · 30d"} />
            {economics.incentivePct != null && (
              <StatTile label="Incentive cost" value={`${economics.incentivePct.toFixed(1)}%`} sub={`${fmt$(economics.incentiveTotal ?? 0)} paid vs captured revenue`} />
            )}
            <StatTile label="Total-loss finds" value={String(economics.totalLossFinds)}
              sub={economics.totalLossNames.length
                ? `${economics.totalLossNames.join(", ")} → sales leads`
                : economics.totalLossLeadsPending != null ? `${(economics.totalLossLeadsPending ?? 0) + (economics.totalLossLeadsAssigned ?? 0)} in the sales pipeline` : "acquisition leads for used-car"} />
          </div>
          {economics.mom && (
            <p className="text-[12px] text-[#6E6C64] mt-3 pt-3 border-t border-[#ECEBE6]">
              vs last month:{" "}
              <span className={cx("font-semibold", economics.mom.found >= 0 ? "text-[#0F6E56]" : "text-[#BA7517]")}>{economics.mom.found >= 0 ? "+" : ""}{economics.mom.found}% found</span>
              {" · "}
              <span className={cx("font-semibold", economics.mom.captured >= 0 ? "text-[#0F6E56]" : "text-[#BA7517]")}>{economics.mom.captured >= 0 ? "+" : ""}{economics.mom.captured}% captured</span>
              {" · "}
              <span className={cx("font-semibold", economics.mom.scans >= 0 ? "text-[#0F6E56]" : "text-[#BA7517]")}>{economics.mom.scans >= 0 ? "+" : ""}{economics.mom.scans}% scans</span>
            </p>
          )}
        </div>
      )}

      {/* Damage-rate strip — same card anatomy, mini-viz on the rate */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Vehicles evaluated" value={String(stripEvaluated)}
          sub={evaluations.hasData
            ? (evaluations.hasPrior
                ? <span className={cx("font-medium", evaluations.trend >= 0 ? "text-emerald-700" : "text-[#993C1D]")}>{evaluations.trend >= 0 ? "+" : ""}{evaluations.trend}% vs prior {rangeLabel}</span>
                : `walkarounds · ${rangeLabel}`)
            : `estimates only — walkaround capture not recorded yet · ${rangeLabel}`} />
        <KpiCard label="Vehicles with damage" value={String(stripWithDamage)} sub={`${d.total} estimates generated`} />
        <KpiCard label="Damage-found rate" value={`${stripRate}%`}
          sub={stripRate <= 40 ? "≈ 1 in 3 vehicles seen" : "of vehicles seen"}
          viz={<FillBar pct={stripRate} maxLabel="100%" />} />
        <KpiCard label={`High-value (>$${HIGH_VALUE_FLOOR / 1000},000)`} value={`${d.highValueCount}`} valueSuffix={`of ${d.total}`}
          sub={`${d.total ? Math.round((d.highValueCount / d.total) * 100) : 0}% of estimates`} />
      </div>

      {filteredEmpty && (
        <div className="animate-element rounded-2xl border border-dashed border-zinc-300 bg-white p-12 text-center mt-4">
          <p className="text-sm font-semibold text-zinc-700 mb-1">Nothing matches the current filters in the last {rangeLabel}</p>
          <p className="text-sm text-[#8B897E] max-w-md mx-auto mb-4">Widen the time range or remove a filter — the chapters below stay in place.</p>
          {hasFilters && <button onClick={onClearDims} className="text-sm font-medium text-[#0F6E56] hover:underline">Clear all filters</button>}
        </div>
      )}
    </ChapterSection>
  )
}
